// src/components/forms/ListingForm.tsx — write the advert for a unit.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useUnitOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, nulled, numberOr, toMajor, toMinor } from './common';

type L = Record<string, any>;

const bedsLabel = (beds: number) => (beds === 0 ? 'Studio' : `${beds} bedroom`);

/** England caps a tenancy deposit at five weeks' rent, six at £50,000 a year or more (the server's tenancy.rules.ts; publishing re-checks it). */
const capWeeks = (monthlyMinor: number) => (monthlyMinor * 12 >= 5_000_000 ? 6 : 5);
const depositCap = (monthlyMinor: number) => Math.round((monthlyMinor * 12 * capWeeks(monthlyMinor)) / 52);

/**
 * A listing is created from a unit, never from a blank form — the rent, the beds and
 * the scheme are already known, so the advert starts written and the agent edits the
 * words rather than re-keying the facts.
 */
export function ListingForm({ open, onClose, listing, unit }: { open: boolean; onClose: () => void; listing?: L; unit?: Record<string, any> }) {
  const isNew = !listing;
  const qc = useQueryClient();
  const vacant = useUnitOptions({ status: 'VACANT' }, isNew && !unit);
  const onNotice = useUnitOptions({ status: 'NOTICE' }, isNew && !unit);

  const seed = (u?: Record<string, any>) => ({
    headline: u ? `${bedsLabel(u.bedrooms ?? 1)} ${String(u.unitType ?? 'apartment').toLowerCase()} at ${u.property?.name ?? ''}`.trim() : '',
    rent: toMajor(u?.marketRentMinor),
    deposit: u?.marketRentMinor ? toMajor(depositCap(Number(u.marketRentMinor))) : '',
    currency: u?.marketRentCurrency ?? 'GBP',
    rentPeriod: (u?.marketRentCurrency ?? 'GBP') === 'AED' ? 'YEAR' : 'MONTH',
  });

  const [form, setForm] = useState(() => {
    if (listing) return {
      unitId: listing.unitId ?? '', headline: listing.headline ?? '', description: listing.description ?? '',
      rent: toMajor(listing.advertisedRentMinor), currency: listing.advertisedRentCurrency ?? 'GBP',
      rentPeriod: listing.rentPeriod ?? 'MONTH', deposit: toMajor(listing.depositMinor),
      availableFrom: listing.availableFrom ?? '', minTermMonths: String(listing.minTermMonths ?? 12),
    };
    const s = seed(unit);
    return { unitId: unit?.id ?? '', description: '', availableFrom: '', minTermMonths: '12', ...s };
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const choices = [...(vacant.data ?? []), ...(onNotice.data ?? [])];
  const chooseUnit = (id: string) => {
    const u = choices.find((c) => c.id === id);
    set({ unitId: id, ...(u ? seed(u) : {}) });
  };

  const core = () => ({
    headline: form.headline.trim(),
    description: nulled(form.description),
    advertisedRentMinor: toMinor(form.rent) ?? 0,
    advertisedRentCurrency: form.currency,
    rentPeriod: form.rentPeriod,
    depositMinor: toMinor(form.deposit) ?? null,
    depositCurrency: form.currency,
    availableFrom: nulled(form.availableFrom),
    minTermMonths: numberOr(form.minTermMonths, 12),
  });

  const save = useMutation({
    mutationFn: async () => (isNew
      ? (await api.post<L>('/listings', { unitId: form.unitId, ...core() })).data
      : (await api.patch<L>(`/listings/${listing!.id}`, core())).data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['/listings'] });
      onClose();
    },
  });

  const rentMinor = toMinor(form.rent) ?? 0;
  const capBreach = form.currency === 'GBP' && form.rentPeriod === 'MONTH' && rentMinor > 0
    && (toMinor(form.deposit) ?? 0) > depositCap(rentMinor);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isNew ? 'New listing' : 'Edit listing'}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!form.unitId || !form.headline.trim() || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Create draft' : 'Save changes'}</Button>
      </>}
    >
      <div className="space-y-4">
        <FormGrid>
          <Wide>
            <Field label="Unit" hint={isNew ? 'Vacant units and units on notice can be advertised' : 'A listing stays with its unit'}>
              {isNew && !unit ? (
                <Select value={form.unitId} onChange={(e) => chooseUnit(e.target.value)} className="w-full">
                  <option value="">Choose a unit…</option>
                  {choices.map((u) => <option key={u.id} value={u.id}>{u.property?.name} · {u.reference} · {bedsLabel(u.bedrooms)}{u.status === 'NOTICE' ? ' (on notice)' : ''}</option>)}
                </Select>
              ) : (
                <Input value={unit ? `${unit.property?.name ?? ''} · ${unit.reference}` : `${listing?.property?.name ?? ''} · ${listing?.unit?.reference ?? ''}`} disabled className="w-full" />
              )}
            </Field>
          </Wide>

          <Wide><Field label="Headline"><Input value={form.headline} onChange={(e) => set({ headline: e.target.value })} placeholder="Two bedroom apartment at Elgar House" className="w-full" /></Field></Wide>
          <Wide><Field label="Description" hint="What the website shows under the photographs"><Textarea rows={4} value={form.description} onChange={(e) => set({ description: e.target.value })} className="w-full" /></Field></Wide>

          <Field label={`Advertised rent (${form.currency})`}><Input value={form.rent} onChange={(e) => set({ rent: e.target.value })} inputMode="decimal" className="w-full" /></Field>
          <Field label="Per"><Select value={form.rentPeriod} onChange={(e) => set({ rentPeriod: e.target.value })} className="w-full"><option value="MONTH">Month</option><option value="YEAR">Year</option></Select></Field>

          <Field label={`Deposit (${form.currency})`} hint={form.currency === 'GBP' ? 'Capped at five weeks’ rent in England' : undefined}>
            <Input value={form.deposit} onChange={(e) => set({ deposit: e.target.value })} inputMode="decimal" className="w-full" />
          </Field>
          <Field label="Currency"><Select value={form.currency} onChange={(e) => set({ currency: e.target.value })} className="w-full"><option value="GBP">GBP</option><option value="AED">AED</option></Select></Field>

          <Field label="Available from" hint="Leave blank for immediately"><Input type="date" value={form.availableFrom} onChange={(e) => set({ availableFrom: e.target.value })} className="w-full" /></Field>
          {form.currency !== 'GBP' && <Field label="Minimum term (months)"><Input value={form.minTermMonths} onChange={(e) => set({ minTermMonths: e.target.value })} inputMode="numeric" className="w-full" /></Field>}
        </FormGrid>

        {capBreach && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            That deposit is over {capWeeks(rentMinor)} weeks’ rent ({toMajor(depositCap(rentMinor))}), the Tenant Fees Act 2019 cap (five weeks, six at £50,000 a year or more). It cannot be published like this.
          </p>
        )}

        <p className="text-[12px] leading-relaxed text-ink-400">Listings are created as drafts. Nothing reaches the website until you publish it.</p>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
